<template>
  <div>
    <!-- Month navigation + legend -->
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="rounded-lg border border-gray-300 px-2 py-1 text-gray-600 hover:bg-gray-50"
          aria-label="Previous month"
          @click="prevMonth"
        >
          ‹
        </button>
        <h2
          class="min-w-[10rem] text-center text-lg font-semibold text-gray-900"
        >
          {{ monthLabel }}
        </h2>
        <button
          type="button"
          class="rounded-lg border border-gray-300 px-2 py-1 text-gray-600 hover:bg-gray-50"
          aria-label="Next month"
          @click="nextMonth"
        >
          ›
        </button>
        <button
          type="button"
          class="ml-2 rounded-lg border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:bg-gray-50"
          @click="goToday"
        >
          Today
        </button>
      </div>

      <!-- Status colour key: only meaningful on a site's tape chart, not the
           all-sites table (which has no bars). -->
      <div v-if="currentSiteId" class="flex flex-wrap items-center gap-3">
        <span
          v-for="item in legend"
          :key="item.status"
          class="flex items-center gap-1.5 text-xs text-gray-500"
        >
          <span class="h-3 w-3 rounded-sm" :class="`bg-${item.color}`" />
          {{ item.label }}
        </span>
      </div>
    </div>

    <!-- Filter bar (mirrors the Site Plans filter row) -->
    <div class="mb-4 flex flex-wrap items-center gap-3">
      <!-- Show: which resource types are in view (sections per-site, columns
           all-sites). -->
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">
        Show
      </span>
      <button
        type="button"
        :aria-pressed="showPitches"
        class="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium"
        :style="
          showPitches
            ? 'background:#16a34a;border-color:#16a34a;color:#fff'
            : 'background:#fff;border-color:#d1d5db;color:#4b5563'
        "
        @click="showPitches = !showPitches"
      >
        <span v-if="showPitches" aria-hidden="true">✓</span>
        Pitches
      </button>
      <button
        type="button"
        :aria-pressed="showEF"
        class="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium"
        :style="
          showEF
            ? 'background:#2c94a7;border-color:#2c94a7;color:#fff'
            : 'background:#fff;border-color:#d1d5db;color:#4b5563'
        "
        @click="showEF = !showEF"
      >
        <span v-if="showEF" aria-hidden="true">✓</span>
        EF
      </button>
      <button
        type="button"
        :aria-pressed="showUnassigned"
        class="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium"
        :style="
          showUnassigned
            ? 'background:#ca8a04;border-color:#ca8a04;color:#fff'
            : 'background:#fff;border-color:#d1d5db;color:#4b5563'
        "
        @click="showUnassigned = !showUnassigned"
      >
        <span v-if="showUnassigned" aria-hidden="true">✓</span>
        Unassigned
      </button>

      <span class="mx-1 h-6 w-px bg-gray-200" aria-hidden="true" />

      <!-- Filter: narrow within whatever types are shown. -->
      <span class="text-xs font-medium uppercase tracking-wide text-gray-400">
        Filter
      </span>
      <button
        type="button"
        :aria-pressed="onlyOutstanding"
        class="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium"
        :style="
          onlyOutstanding
            ? 'background:#1d4ed8;border-color:#1d4ed8;color:#fff'
            : 'background:#fff;border-color:#d1d5db;color:#4b5563'
        "
        @click="onlyOutstanding = !onlyOutstanding"
      >
        <span v-if="onlyOutstanding" aria-hidden="true">✓</span>
        Outstanding
      </button>
      <input
        v-model="search"
        type="search"
        :placeholder="currentSiteId ? 'name or ref…' : 'site name…'"
        class="focus:border-primary focus:ring-primary w-44 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1"
      />
      <span class="text-sm text-gray-500">
        {{ visibleCount }} booking{{ visibleCount === 1 ? "" : "s" }}
        <span v-if="currentSiteId && bookingsFetching" class="text-gray-400"
          >(updating…)</span
        >
      </span>
    </div>

    <!-- All-sites overview: one row per site, click to drill into its calendar.
         Rendered as soon as the site list loads; booking counts fill in when
         the (larger) all-sites bookings query returns. -->
    <template v-if="!currentSiteId">
      <div v-if="sitesFetching" class="py-16 text-center text-gray-500">
        Loading…
      </div>
      <div v-else class="overflow-hidden rounded-lg border border-gray-200">
        <!-- Grouped header: "Bookings" spans the shown type columns -->
        <div
          v-if="visibleTypeCount"
          class="flex border-b border-gray-100 bg-gray-50 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
        >
          <div class="flex-1 px-3 pt-1.5" />
          <div
            class="px-3 pt-1.5 text-center"
            :style="{ width: `${visibleTypeCount * 6}rem` }"
          >
            Bookings
            <span v-if="bookingsFetching" class="normal-case text-gray-400"
              >(updating…)</span
            >
          </div>
          <div class="w-28 px-3 pt-1.5" />
          <div class="w-8 shrink-0" />
        </div>
        <div
          class="flex border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500"
        >
          <div class="flex-1 px-3 pb-2">Site</div>
          <div v-if="showPitches" class="w-24 px-3 pb-2 text-right">
            Pitches
          </div>
          <div v-if="showEF" class="w-24 px-3 pb-2 text-right">EF</div>
          <div v-if="showUnassigned" class="w-24 px-3 pb-2 text-right">
            Unassigned
          </div>
          <div class="w-28 px-3 pb-2 text-right">Outstanding</div>
          <div class="w-8 shrink-0" />
        </div>
        <button
          v-for="row in siteRows"
          :key="row.site.id"
          type="button"
          class="flex w-full items-center border-b border-gray-100 text-left hover:bg-gray-50"
          @click="selectSite(row.site.id)"
        >
          <div
            class="flex-1 truncate px-3 py-2 text-sm font-medium text-gray-800"
          >
            {{ row.site.name }}
          </div>
          <div
            v-if="showPitches"
            class="w-24 px-3 py-2 text-right text-sm text-gray-700"
          >
            {{ row.pitches || "—" }}
          </div>
          <div
            v-if="showEF"
            class="w-24 px-3 py-2 text-right text-sm"
            :class="row.ef ? 'font-medium' : 'text-gray-400'"
            :style="row.ef ? 'color:#2c94a7' : undefined"
          >
            {{ row.ef || "—" }}
          </div>
          <div
            v-if="showUnassigned"
            class="w-24 px-3 py-2 text-right text-sm"
            :class="row.unassigned ? 'text-amber font-medium' : 'text-gray-400'"
          >
            {{ row.unassigned || "—" }}
          </div>
          <div
            class="w-28 px-3 py-2 text-right text-sm"
            :class="
              row.outstanding ? 'font-medium text-gray-700' : 'text-gray-400'
            "
          >
            {{ row.outstanding || "—" }}
          </div>
          <div class="w-8 shrink-0 pr-3 text-right text-gray-400">›</div>
        </button>
        <div
          v-if="!siteRows.length"
          class="px-3 py-10 text-center text-gray-500"
        >
          No sites match your filters.
        </div>
      </div>
    </template>

    <!-- Single-site states -->
    <div v-else-if="isLoading" class="py-16 text-center text-gray-500">
      Loading calendar…
    </div>
    <div
      v-else-if="!pitches.length && !efUnits.length"
      class="py-16 text-center text-gray-500"
    >
      This site has no pitches or units to display.
    </div>

    <!-- Tape chart: one row per pitch, one column per day of the month -->
    <div v-else class="overflow-x-auto rounded-lg border border-gray-200">
      <div
        :style="{
          minWidth: `calc(${LABEL_W}px + ${daysInMonth} * ${DAY_W}px)`,
        }"
      >
        <!-- Day-number ruler (shared by every section for column alignment) -->
        <div class="flex border-b border-gray-200 bg-gray-50">
          <div
            class="shrink-0 border-r border-gray-200 px-2 py-1"
            :style="{ width: `${LABEL_W}px` }"
          />
          <div class="flex-1" :style="gridStyle">
            <div
              v-for="d in dayHeaders"
              :key="d.day"
              class="border-r border-gray-100 py-1 text-center"
              :style="d.isToday ? todayHeaderStyle : undefined"
            >
              <div class="text-[10px] leading-none text-gray-400">
                {{ d.dow }}
              </div>
              <div class="text-xs font-medium leading-tight text-gray-600">
                {{ d.day }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pitches section (only when Pitches is shown and has rows) -->
        <div
          v-if="pitchRows.length"
          class="flex items-center gap-2 border-b border-t border-gray-200 px-2 py-1"
          style="background-color: rgba(22, 163, 74, 0.08)"
        >
          <span class="text-xs font-semibold" style="color: #16a34a"
            >Pitches</span
          >
        </div>

        <!-- One row per pitch -->
        <div
          v-for="row in pitchRows"
          :key="row.pitch.id"
          class="flex border-b border-gray-100 hover:bg-gray-50/60"
        >
          <div
            class="shrink-0 truncate border-r border-gray-200 px-2 py-2 text-sm font-medium text-gray-800"
            :style="{ width: `${LABEL_W}px` }"
            :title="`Pitch ${row.pitch.name} — ${row.pitch.pitchTypeName}`"
          >
            {{ row.pitch.name }}
          </div>

          <!-- Day area: background grid + absolutely-placed booking bars -->
          <div class="relative flex-1">
            <div class="h-10" :style="gridStyle">
              <div
                v-for="d in dayHeaders"
                :key="d.day"
                class="border-r border-gray-100"
                :style="d.isToday ? todayCellStyle : undefined"
              />
            </div>

            <button
              v-for="bar in row.bars"
              :key="bar.id"
              type="button"
              class="absolute top-1 flex h-8 items-center overflow-hidden rounded px-1 text-left text-[11px] font-medium shadow-sm"
              :class="barClass(bar)"
              :style="barStyle(bar)"
              :title="bar.title"
              @click="openBooking(bar.id)"
            >
              <span v-if="bar.clippedLeft" class="mr-0.5 flex-none">‹</span>
              <span class="truncate">{{ bar.label }}</span>
              <span v-if="bar.clippedRight" class="ml-auto flex-none pl-0.5"
                >›</span
              >
            </button>
          </div>
        </div>

        <!-- Experience Freedom units (glamping): one row per unit -->
        <template v-if="unitRows.length">
          <div
            class="flex items-center gap-2 border-b border-t border-gray-200 px-2 py-1"
            style="background-color: rgba(44, 148, 167, 0.1)"
          >
            <span class="text-xs font-semibold" style="color: #2c94a7">
              Experience Freedom
            </span>
            <span class="text-xs text-gray-500">units</span>
          </div>

          <div
            v-for="row in unitRows"
            :key="row.unit.id"
            class="flex border-b border-gray-100 hover:bg-gray-50/60"
          >
            <div
              class="shrink-0 truncate border-r border-gray-200 px-2 py-2 text-sm font-medium text-gray-800"
              :style="{ width: `${LABEL_W}px` }"
              :title="`${row.unit.name} — ${row.unit.pitchTypeName}`"
            >
              {{ row.unit.name }}
            </div>

            <div class="relative flex-1">
              <div class="h-10" :style="gridStyle">
                <div
                  v-for="d in dayHeaders"
                  :key="d.day"
                  class="border-r border-gray-100"
                  :style="d.isToday ? todayCellStyle : undefined"
                />
              </div>

              <button
                v-for="bar in row.bars"
                :key="bar.id"
                type="button"
                class="absolute top-1 flex h-8 items-center overflow-hidden rounded px-1 text-left text-[11px] font-medium shadow-sm"
                :class="barClass(bar)"
                :style="barStyle(bar)"
                :title="bar.title"
                @click="openBooking(bar.id)"
              >
                <span v-if="bar.clippedLeft" class="mr-0.5 flex-none">‹</span>
                <span class="truncate">{{ bar.label }}</span>
                <span v-if="bar.clippedRight" class="ml-auto flex-none pl-0.5"
                  >›</span
                >
              </button>
            </div>
          </div>
        </template>

        <!-- Unassigned lanes, kept below the resource rows so the assigned view
             leads. Bookings with no pitch/unit decided yet. -->
        <template v-if="unassignedLanes.length">
          <div
            class="flex items-center gap-2 border-b border-t border-gray-200 px-2 py-1"
            style="background-color: rgba(202, 138, 4, 0.1)"
          >
            <span class="text-amber text-xs font-semibold">Unassigned</span>
            <span class="text-xs text-gray-500">not assigned yet</span>
          </div>

          <div
            v-for="(lane, laneIndex) in unassignedLanes"
            :key="`unassigned-${laneIndex}`"
            class="flex border-b border-gray-100"
            style="background-color: rgba(202, 138, 4, 0.06)"
          >
            <div
              class="shrink-0 border-r border-gray-200"
              :style="{ width: `${LABEL_W}px` }"
            />

            <div class="relative flex-1">
              <div class="h-10" :style="gridStyle">
                <div
                  v-for="d in dayHeaders"
                  :key="d.day"
                  class="border-r border-gray-100"
                  :style="d.isToday ? todayCellStyle : undefined"
                />
              </div>

              <button
                v-for="bar in lane"
                :key="bar.id"
                type="button"
                class="absolute top-1 flex h-8 items-center overflow-hidden rounded px-1 text-left text-[11px] font-medium shadow-sm"
                :class="barClass(bar)"
                :style="barStyle(bar)"
                :title="bar.title"
                @click="openBooking(bar.id)"
              >
                <span v-if="bar.clippedLeft" class="mr-0.5 flex-none">‹</span>
                <span class="truncate">{{ bar.label }}</span>
                <span v-if="bar.clippedRight" class="ml-auto flex-none pl-0.5"
                  >›</span
                >
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <p v-if="truncated" class="text-amber mt-2 text-xs">
      Showing the first {{ effectiveLimit }} bookings for this month — narrow
      the month to see all of them.
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  BOOKING_STATUS_MAP,
  type BookingStatus,
  type Pitch,
} from "@/services/bookings/bookings";
import {
  useCalendarBookings,
  type CalendarBooking,
} from "@/services/bookings/listCalendarBookings";
import { usePitches } from "@/services/bookings/listPitches";
import { useCurrentSite } from "@/services/locations/useCurrentSite";
import { useSites, type Site } from "@/services/locations/listSites";

// Column geometry (kept in sync between the header row and every pitch row so
// the day columns line up; also feeds the min-width that forces horizontal
// scroll on narrow screens).
const LABEL_W = 80;
const DAY_W = 32;
const LIMIT = 1000;
// The all-sites overview needs every site's bookings for the month at once.
const ALL_SITES_LIMIT = 3000;

const router = useRouter();
const { id: currentSiteId } = useCurrentSite();

// Current month by default; prev/next step a whole month.
const cursor = ref(DateFns.startOfMonth(new Date()));
const monthStart = computed(() => DateFns.startOfMonth(cursor.value));
const monthEnd = computed(() => DateFns.endOfMonth(cursor.value));
const daysInMonth = computed(() => DateFns.getDaysInMonth(cursor.value));
const monthLabel = computed(() => DateFns.format(cursor.value, "MMMM yyyy"));

const prevMonth = () => (cursor.value = DateFns.subMonths(cursor.value, 1));
const nextMonth = () => (cursor.value = DateFns.addMonths(cursor.value, 1));
const goToday = () => (cursor.value = DateFns.startOfMonth(new Date()));

// Bookings overlapping the visible month, for the current site (useBookings
// injects the site filter itself). The server's default date filter is an
// overlap test, so stays that straddle the month boundary are included.
const effectiveLimit = computed(() =>
  currentSiteId.value ? LIMIT : ALL_SITES_LIMIT,
);
const bookingsQuery = computed(() => ({
  fromDt: monthStart.value,
  untilDt: monthEnd.value,
  limit: effectiveLimit.value,
  offset: 0,
}));
// No periodic refetch here — the tape chart is expensive to re-render and a
// month view doesn't need second-by-second freshness (changing month/filters
// or reopening the view refetches anyway).
const { data: bookingsData, isFetching: bookingsFetching } =
  useCalendarBookings(bookingsQuery);
const { data: pitchesData, isLoading: pitchesLoading } =
  usePitches(currentSiteId);
const { data: sitesData, isFetching: sitesFetching } = useSites();

// Gate the blocking "Loading calendar…" (single-site view) on a genuine cold
// load only — a query that is pending with *no* data at all (isLoading), not
// one merely revalidating (isFetching). With the persisted cache + staleTime, a
// reload restores the previous month instantly, so we paint the tape chart and
// refetch behind an "(updating…)" hint instead of blanking the whole chart.
// The chart's rows come from pitches/units, so those (not bookings) decide when
// there's enough to draw; the bars fill in when the bookings query resolves.
const isLoading = computed(() => pitchesLoading.value);
// One query covers both sections: Experience Freedom units are Pitches under an
// EF-defaulting PitchType (migration 0084), so /pitchs returns them alongside
// touring pitches and the two sections are a partition of the same list. The
// API already returns them in natural name order (Pitch.objects.order_by_name).
const allPitches = computed<Pitch[]>(() => pitchesData.value ?? []);
const pitches = computed<Pitch[]>(() =>
  allPitches.value.filter((p) => !p.isExperienceFreedom),
);
const efUnits = computed<Pitch[]>(() =>
  allPitches.value.filter((p) => p.isExperienceFreedom),
);
const sites = computed<Site[]>(() => sitesData.value ?? []);
const bookings = computed<CalendarBooking[]>(
  () => bookingsData.value?.items ?? [],
);
const truncated = computed(
  () => (bookingsData.value?.count ?? 0) > bookings.value.length,
);

const selectSite = (siteId: string) => {
  currentSiteId.value = siteId;
};

const gridStyle = computed(() => ({
  display: "grid",
  gridTemplateColumns: `repeat(${daysInMonth.value}, minmax(0, 1fr))`,
}));

const todayHeaderStyle = "background-color: rgba(29, 78, 216, 0.12)";
const todayCellStyle = "background-color: rgba(29, 78, 216, 0.06)";

const dayHeaders = computed(() => {
  const today = new Date();
  return Array.from({ length: daysInMonth.value }, (_, i) => {
    const date = DateFns.addDays(monthStart.value, i);
    return {
      day: i + 1,
      dow: DateFns.format(date, "EEEEE"), // single-letter weekday
      isToday: DateFns.isSameDay(date, today),
    };
  });
});

type Bar = {
  id: string;
  leftPct: number;
  widthPct: number;
  color: string;
  label: string;
  title: string;
  clippedLeft: boolean;
  clippedRight: boolean;
  provisional: boolean;
  // Visible column bounds [startCol, endCol] within the month, used to
  // first-fit pack the unassigned bookings into non-overlapping lanes.
  startCol: number;
  endCol: number;
};

// Bars are drawn per night: a stay occupies nights [start_date, end_date), so
// the last occupied night is the day before end_date (the departure day is a
// changeover, not an occupied night). This makes back-to-back stays butt up
// instead of overlapping — the CAMC booking convention (nights = end - start).
// `provisional` bars are future bookings with no pitch decided yet; they render
// distinctly (dashed/hatched) and live in the "Unassigned" lanes, not on a
// real pitch row.
const makeBar = (
  b: CalendarBooking,
  ms: Date,
  me: Date,
  n: number,
  provisional = false,
): Bar | null => {
  const start = DateFns.parseISO(b.startDate);
  const end = DateFns.parseISO(b.endDate);
  const lastNight = DateFns.subDays(end, 1);

  const firstVisible = DateFns.max([start, ms]);
  const lastVisible = DateFns.min([lastNight, me]);
  if (DateFns.isAfter(firstVisible, lastVisible)) return null;

  const startCol = DateFns.differenceInCalendarDays(firstVisible, ms);
  const spanCols =
    DateFns.differenceInCalendarDays(lastVisible, firstVisible) + 1;

  const surname = b.customer?.lastName ?? "";
  const displayName = surname || b.bookingRef;
  // Total nights of the stay (not just the visible slice) — shown on the bar.
  const nights = Math.max(1, DateFns.differenceInCalendarDays(end, start));
  const statusLabel = BOOKING_STATUS_MAP[b.status]?.label ?? b.status;

  return {
    id: b.id,
    leftPct: (startCol / n) * 100,
    widthPct: (spanCols / n) * 100,
    color: BOOKING_STATUS_MAP[b.status]?.color ?? "gray2",
    label: `${displayName} · ${nights}n`,
    title: `${b.bookingRef}${surname ? ` — ${surname}` : ""} · ${nights} night${
      nights === 1 ? "" : "s"
    } · ${DateFns.format(start, "d MMM")} → ${DateFns.format(
      end,
      "d MMM",
    )} · ${statusLabel}${provisional ? " · not assigned yet" : ""}`,
    clippedLeft: DateFns.isBefore(start, ms),
    clippedRight: DateFns.isAfter(lastNight, me),
    provisional,
    startCol,
    endCol: startCol + spanCols - 1,
  };
};

// Filter bar state. The section toggles (Pitches / EF / Unassigned) show or
// hide each row group so you can focus on one resource type.
const search = ref("");
const showPitches = ref(true);
const showEF = ref(true);
const showUnassigned = ref(true);
const onlyOutstanding = ref(false);

// How many of the three type columns are shown (drives the all-sites "Bookings"
// group-header width).
const visibleTypeCount = computed(
  () =>
    (showPitches.value ? 1 : 0) +
    (showEF.value ? 1 : 0) +
    (showUnassigned.value ? 1 : 0),
);

const isEFBooking = (b: CalendarBooking): boolean =>
  b.bookingType === "experience_freedom";

// Rows are keyed by pitch id — EF units and touring pitches are both Pitches, so
// a single accessor places either. Falls back to the reserved pitch when a stay
// is pre-allocated but not yet checked in.
const bookingPitchId = (b: CalendarBooking): string | null =>
  b.pitch?.id ?? b.reserved?.id ?? null;

const bookingPitchName = (b: CalendarBooking): string | null =>
  b.pitch?.name ?? b.reserved?.name ?? null;

// A booking is "assigned" when it has a resource: a pitch (touring) or an EF
// unit (glamping). Anything else is still unassigned.
const bookingIsAssigned = (b: CalendarBooking): boolean =>
  bookingPitchId(b) != null;

// Which row group a booking belongs to.
type Section = "pitch" | "ef" | "unassigned";
const bookingSection = (b: CalendarBooking): Section =>
  !bookingIsAssigned(b) ? "unassigned" : isEFBooking(b) ? "ef" : "pitch";

const matchesText = (b: CalendarBooking): boolean => {
  const q = search.value.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    b.bookingRef,
    b.customer?.firstName,
    b.customer?.lastName,
    bookingPitchName(b) ?? b.pitchNumber,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
};

const matchesBalance = (b: CalendarBooking): boolean =>
  !onlyOutstanding.value || (b.balanceDue ?? 0) > 0;

// Drop-empty (hiding empty rows within a shown section) only applies to the
// content filters — search / outstanding — not to the section toggles, which
// hide whole sections rather than individual rows.
const filtersActive = computed(
  () => search.value.trim() !== "" || onlyOutstanding.value,
);

// One row per pitch, bookings that have a (real or reserved) pitch placed on it.
const pitchRows = computed(() => {
  const ms = monthStart.value;
  const me = monthEnd.value;
  const n = daysInMonth.value;

  // Group each booking onto its assigned pitch (falling back to a reserved
  // pitch when pre-allocated but not yet checked in). EF bookings are on EF
  // pitches, which this section excludes — they get their own band below.
  if (!showPitches.value) return [];

  const byPitch = new Map<string, CalendarBooking[]>();
  for (const b of bookings.value) {
    if (isEFBooking(b)) continue;
    const pitchId = bookingPitchId(b);
    if (pitchId == null) continue;
    if (!matchesText(b) || !matchesBalance(b)) continue;
    const list = byPitch.get(pitchId);
    if (list) list.push(b);
    else byPitch.set(pitchId, [b]);
  }

  // Already in natural name order from the API — don't re-sort.
  const allRows = pitches.value.map((pitch) => ({
    pitch,
    bars: (byPitch.get(pitch.id) ?? [])
      .map((b) => makeBar(b, ms, me, n))
      .filter((bar): bar is Bar => bar !== null),
  }));

  // With a filter active, hide pitch rows that have no matching bookings so the
  // result reads as a focused list rather than a mostly-empty grid.
  return filtersActive.value
    ? allRows.filter((row) => row.bars.length > 0)
    : allRows;
});

// One row per Experience Freedom unit (glamping) with its EF bookings. Same
// tape-chart layout as pitches, in a separate "Experience Freedom" section.
const unitRows = computed(() => {
  const ms = monthStart.value;
  const me = monthEnd.value;
  const n = daysInMonth.value;

  if (!showEF.value) return [];

  const byUnit = new Map<string, CalendarBooking[]>();
  for (const b of bookings.value) {
    if (!isEFBooking(b)) continue;
    const unitId = bookingPitchId(b);
    if (unitId == null) continue;
    if (!matchesText(b) || !matchesBalance(b)) continue;
    const list = byUnit.get(unitId);
    if (list) list.push(b);
    else byUnit.set(unitId, [b]);
  }

  // Already in natural name order from the API — don't re-sort.
  const allRows = efUnits.value.map((unit) => ({
    unit,
    bars: (byUnit.get(unit.id) ?? [])
      .map((b) => makeBar(b, ms, me, n))
      .filter((bar): bar is Bar => bar !== null),
  }));

  return filtersActive.value
    ? allRows.filter((row) => row.bars.length > 0)
    : allRows;
});

// Bookings with no resource decided yet — an unallocated pitch OR an EF booking
// with no unit — shown in dedicated "Unassigned" lanes below the rows, first-fit
// packed so overlapping stays stack into extra lanes rather than overlapping.
const unassignedLanes = computed<Bar[][]>(() => {
  if (!showUnassigned.value) return [];
  const ms = monthStart.value;
  const me = monthEnd.value;
  const n = daysInMonth.value;

  const bars = bookings.value
    .filter((b) => !bookingIsAssigned(b) && matchesText(b) && matchesBalance(b))
    .map((b) => makeBar(b, ms, me, n, true))
    .filter((bar): bar is Bar => bar !== null)
    .sort((a, b) => a.startCol - b.startCol || a.endCol - b.endCol);

  const lanes: Bar[][] = [];
  const laneEnds: number[] = []; // last used column per lane
  for (const bar of bars) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (bar.startCol > laneEnds[i]) {
        lanes[i].push(bar);
        laneEnds[i] = bar.endCol;
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([bar]);
      laneEnds.push(bar.endCol);
    }
  }
  return lanes;
});

// All-sites overview: one row per site with the month's booking totals
// (respecting the active filters). Clicking a row selects that site and drops
// into its calendar. Totals cover both touring (pitch) and Experience Freedom
// (unit) bookings.
type SiteTally = {
  count: number;
  pitches: number;
  ef: number;
  unassigned: number;
  outstanding: number;
};
type SiteRow = { site: Site } & SiteTally;
const emptyTally = (): SiteTally => ({
  count: 0,
  pitches: 0,
  ef: 0,
  unassigned: 0,
  outstanding: 0,
});
const siteRows = computed<SiteRow[]>(() => {
  const tally = new Map<string, SiteTally>();
  for (const b of bookings.value) {
    // In all-sites the search box filters by site name (below), not by
    // customer — so the counts here only honour the Outstanding filter.
    if (!matchesBalance(b)) continue;
    const section = bookingSection(b);
    if (section === "pitch" && !showPitches.value) continue;
    if (section === "ef" && !showEF.value) continue;
    if (section === "unassigned" && !showUnassigned.value) continue;
    const t = tally.get(b.siteId) ?? emptyTally();
    t.count += 1;
    if (section === "pitch") t.pitches += 1;
    else if (section === "ef") t.ef += 1;
    else t.unassigned += 1;
    if ((b.balanceDue ?? 0) > 0) t.outstanding += 1;
    tally.set(b.siteId, t);
  }

  const q = search.value.trim().toLowerCase();
  const rows = [...sites.value]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((site) => ({ site, ...(tally.get(site.id) ?? emptyTally()) }));

  // List sites that have bookings in the shown types (the ticks act as an OR
  // filter), optionally narrowed to a site-name search.
  return rows.filter(
    (r) => r.count > 0 && (!q || r.site.name.toLowerCase().includes(q)),
  );
});

// Count of bookings currently drawn: per-pitch bars when a site is selected,
// otherwise the sum across the all-sites overview.
const visibleCount = computed(() =>
  currentSiteId.value
    ? pitchRows.value.reduce((sum, row) => sum + row.bars.length, 0) +
      unitRows.value.reduce((sum, row) => sum + row.bars.length, 0) +
      unassignedLanes.value.reduce((sum, lane) => sum + lane.length, 0)
    : siteRows.value.reduce((sum, row) => sum + row.count, 0),
);

const legend: { status: BookingStatus; label: string; color: string }[] = (
  [
    "scheduled",
    "expected",
    "arrived",
    "checkedin",
    "departed",
    "overstayed",
  ] as BookingStatus[]
).map((status) => ({
  status,
  label: BOOKING_STATUS_MAP[status].label,
  color: BOOKING_STATUS_MAP[status].color,
}));

// Provisional (unassigned) bars render dashed + hatched with dark text so they
// read as "not a real allocation"; assigned bars are a solid status colour.
const HATCH =
  "repeating-linear-gradient(45deg, rgba(87,83,78,0.10) 0, rgba(87,83,78,0.10) 5px, rgba(87,83,78,0.22) 5px, rgba(87,83,78,0.22) 10px)";

const barClass = (bar: Bar) =>
  bar.provisional
    ? "border border-dashed border-gray-500 text-gray-700"
    : `bg-${bar.color} text-white`;

const barStyle = (bar: Bar) => ({
  left: `${bar.leftPct}%`,
  width: `${bar.widthPct}%`,
  ...(bar.provisional ? { background: HATCH } : {}),
});

const openBooking = (bookingId: string) =>
  router.push({ name: "bookingInstance", params: { bookingId } });
</script>
